import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import type { Edge, Node } from "reactflow"
import { useReactFlow, useStoreApi } from "reactflow"

import CachedIcon from "@mui/icons-material/Cached"
import SearchIcon from "@mui/icons-material/Search"
import { Grid, Link } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Chip, InputText, NotificationUtils, RadioButton } from "@synapse-analytics/synapse-ui"
import { v4 as uuidv4 } from "uuid"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { BaseSimpleDialog } from "../../../../components/dialogs/BaseSimpleDialog"
import { getTheme } from "../../../../hooks/UseTheme"
import { OptionsWithExplanationDialog } from "../../../Scorecards"
import type { LoopStartNodeProps, SchemaFeature } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { getDescendantNodes } from "../../utils/workflowHelpers"
import { WorkflowExample } from "../examples/WorkflowExample"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

import styles from "./Nodes.module.scss"

/**
 * LoopStartNode component renders a card representing the start of a loop in a workflow.
 * It can be displayed in different views depending on the `menuView` prop.
 * The component includes functionality for updating loop names, changing strategies, and removing nodes.
 *
 * @param {string} id - The optional ID of the node.
 * @param {boolean} menuView - A boolean indicating if the node is being viewed in a menu.
 * @param {record} data - Optional data object containing the updateGraph function, readMode, strategy, loopName, loopId, schema, and formik.
 *
 * @returns {React.ReactElement} The rendered LoopStartNode component.
 */
export function LoopStartNode(props: Readonly<LoopStartNodeProps>): React.ReactElement {
  // Prop destructuring
  const { menuView, data, id } = props
  const readMode = data?.readMode
  const nodeId = id
  const shouldDisplayStrategyOptions =
    !readMode && !menuView && props?.data?.input_lists && props?.data?.input_lists?.length > 1

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "loopStartNode",
  })

  // React Flow hooks
  // Instance of our state management that share our nodes and edges across all component
  const store = useStoreApi()
  // This is react-flow helper function to delete passed nodes as well as all the connected edges from the passed nodes
  const { getEdges } = useReactFlow()
  const { setNodes, getNodes, edges } = store.getState()

  // State hooks
  const [isExplanationDialogOpen, setIsExplanationDialogOpen] = useState<boolean>(false)
  const [isConfirmRemoveNodeDialogOpen, setIsConfirmRemoveNodeDialogOpen] = useState<boolean>(false)
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>([...(props?.data?.input_lists ?? [])])
  const [value, setValue] = useState<string | number>("")

  const currentNode = getNodes()?.find((node) => node.id === props?.id)

  const loopEndNode = getNodes().find(
    (node) => node.data?.parentLoopId === currentNode?.data?.loopId && node?.data?.nodeType === "LoopEndNode",
  )

  const currentSchema = useMemo(() => {
    return !readMode ? props?.data?.formik?.values?.features : props?.data?.schema?.features
  }, [props?.data?.formik?.values?.features, props?.data?.schema?.features, readMode])

  const computedFeatureNames = useMemo(() => {
    return props?.data?.formik?.values?.computedFeatures
      ?.filter((item: { uuid: string; value: string }) => item?.value?.length > 0)
      ?.map((feat: { uuid: string; value: string }) => feat?.value)
  }, [props?.data?.formik?.values?.computedFeatures])

  // Metadata
  const metadata = nodeHelpers.getNodeMetadata("LoopStartNode")

  // state contains all features (computed, schema)
  const [allFeatures, setAllFeatures] = useState([
    ...(currentSchema ?? []),
    ...(computedFeatureNames?.length > 0
      ? computedFeatureNames?.map((name: string) => ({
          name,
          type: "NUMBER",
          is_required: false,
          new: false,
          id: uuidv4(),
          source: "workflow",
        }))
      : []),
  ])

  const updateNodeStrategy = (newStrategy: string): void => {
    setNodes(
      getNodes().map((node) => {
        const newNode = { ...node }
        if (node.id === nodeId) {
          newNode.data = {
            ...newNode.data,
            strategy: newStrategy,
          }
        }
        return newNode
      }),
    )
  }

  /**
   * Handles the removal of a loop start node along with its associated nodes and edges.
   * This function removes the loop start node, loop end node, and all nodes and edges between them.
   * It also updates the graph by reassigning edges to maintain connectivity.
   */
  const handleDeleteLoopStartNode = (): void => {
    // Get all descendant nodes of the loop start node
    const descendantNodes = getDescendantNodes(getNodes(), props?.id as string, "loop-start")

    // getting the nodeId that loop end node was poiting to
    const targetNodeOfLoopEndNode = getEdges()?.find((edge) => edge?.source === loopEndNode?.id)?.target

    // Add the loop start node to the list of nodes to delete
    descendantNodes.push(currentNode as Node)

    const remainingNodes = getNodes().filter(
      (node) => !descendantNodes.some((deletedNode) => deletedNode.id === node.id),
    )

    const targetNodeOfLoopEndNodeIndex = remainingNodes?.findIndex((node) => node?.id === targetNodeOfLoopEndNode)

    if (remainingNodes[targetNodeOfLoopEndNodeIndex] && targetNodeOfLoopEndNodeIndex !== -1) {
      remainingNodes[targetNodeOfLoopEndNodeIndex].data = {
        ...remainingNodes[targetNodeOfLoopEndNodeIndex]?.data,
        parentId: currentNode?.data?.parentId,
      }
    }

    // Create a Set of descendant node IDs for faster lookup
    const descendantNodeIds = new Set(descendantNodes.map((node) => node?.id))

    // Find all edges connected to the nodes to be deleted
    const edgesToDelete = edges.filter(
      (edge) => descendantNodeIds.has(edge.source) || descendantNodeIds.has(edge.target),
    )

    // Find the remaining edges
    const edgesToDeleteIds = new Set(edgesToDelete.map((edge) => edge.id))

    const remainingEdges = edges.filter((edge) => !edgesToDeleteIds.has(edge.id))

    const originalEdge = edgesToDelete?.find((edge) => edge.target === nodeId)

    const originalSourceLoopEndNodeEdge = edgesToDelete?.find((edge) => edge.source === loopEndNode?.id)

    const modifiedEdge: Edge = {
      ...originalEdge,
      id: originalEdge?.id as string,
      sourceHandle: originalEdge?.sourceHandle,
      source: originalEdge?.source as string,
      target: originalSourceLoopEndNodeEdge?.target as string,
    }

    if (props?.data?.updateGraph) {
      props?.data?.updateGraph({
        dataAfterDeletion: {
          nodes: remainingNodes,
          edges:
            modifiedEdge?.data?.text === "true" ? [modifiedEdge, ...remainingEdges] : [...remainingEdges, modifiedEdge],
        },
      })
    }

    setIsConfirmRemoveNodeDialogOpen(false)

    NotificationUtils.toast(t("notifications.loopRemoved"), {
      severity: "success",
    })
  }

  const handleLoopNameChange = (e: InputChangeEvent<Element>): void => {
    setNodes(
      getNodes().map((node) => {
        const newNode = { ...node }
        if (node.id === nodeId) {
          newNode.data = {
            ...newNode.data,
            loopName: e.target.value,
          }
        }

        // setting the loop name in the loop end node
        if (node.data?.parentLoopId === props?.data?.loopId) {
          newNode.data = {
            ...newNode.data,
            parentLoopName: e.target.value,
          }
        }
        return newNode
      }),
    )
  }

  const handleAddNewOption = (): void => {
    const newFeat = {
      name: value,
      type: "LIST",
      is_required: true,
      id: uuidv4(),
      source: "workflow",
      new: true,
    }

    if (selectedValues.includes(value)) return

    if (value) {
      if (!computedFeatureNames.includes(value)) {
        props?.data?.formik?.setFieldValue("features", [...(props?.data?.formik?.values?.features ?? []), newFeat])
      }

      setSelectedValues((prev: (string | number)[]) => [...prev, value])

      setNodes(
        getNodes().map((node) => {
          const newNode = { ...node }
          if (node.id === nodeId) {
            newNode.data = {
              ...newNode.data,
              input_lists: [...((props?.data?.input_lists as string[]) ?? []), value],
            }
          }
          return newNode
        }),
      )
    }
  }

  // Update allFeatures whenever currentSchema or computedFeatureNames change
  useEffect(() => {
    const computedFeatures =
      computedFeatureNames?.map((name: string) => ({
        name,
        type: "UNDEFINED",
        is_required: false,
        new: false,
        id: uuidv4(),
        source: "workflow",
      })) || []

    const updatedAllFeatures = [
      ...(currentSchema?.filter((feat: SchemaFeature) => !!feat?.name) ?? []),
      ...(computedFeatures ?? []),
    ]

    setAllFeatures(updatedAllFeatures)
  }, [currentSchema, computedFeatureNames])

  return (
    <Fragment>
      {isConfirmRemoveNodeDialogOpen && (
        <BaseSimpleDialog
          isLoading={false}
          onAccept={handleDeleteLoopStartNode}
          open
          onClose={() => setIsConfirmRemoveNodeDialogOpen(false)}
          mode="remove-loop-node"
          name=""
        />
      )}

      {isExplanationDialogOpen && (
        <OptionsWithExplanationDialog
          setSelectedOption={(strategy) =>
            setNodes(
              getNodes().map((node) => {
                const newNode = { ...node }
                if (node.id === nodeId) {
                  newNode.data = {
                    ...newNode.data,
                    strategy,
                  }
                }
                return newNode
              }),
            )
          }
          selectedOption={props?.data?.strategy}
          dialogTitle={t("dialog.title")}
          options={[
            {
              value: t("dialog.options.parallel.value"),
              label: t("dialog.options.parallel.label"),
              description: t("dialog.options.parallel.description"),
              component: <WorkflowExample strategy="Parallel" />,
            },
            {
              value: t("dialog.options.combined.value"),
              label: t("dialog.options.combined.label"),
              description: t("dialog.options.combined.description"),
              component: <WorkflowExample strategy="Combined" />,
            },
          ]}
          open
          onClose={() => setIsExplanationDialogOpen(false)}
          shouldEnableSelectingMethods={!readMode}
        />
      )}

      <BaseWorkflowNode
        id={props?.id as string}
        menuView={menuView}
        readMode={readMode ?? undefined}
        nodeType="LoopStartNode"
        title={t("nodeTitle")}
        iconProps={{
          element: <CachedIcon sx={{ color: theme.palette.base.text[1] }} />,
          className: metadata.iconClassName,
        }}
        handlesProps={{ showTarget: !menuView, showSource: !menuView }}
        onRemove={() => setIsConfirmRemoveNodeDialogOpen(true)}
        width={menuView ? 300 : 350}
        headerAction={
          shouldDisplayStrategyOptions && (
            <Link className="explainAnchor" style={{ lineHeight: "16px", fontSize: "12px" }} underline="none">
              {readMode && props?.data?.strategy
                ? `${props?.data?.strategy?.charAt(0)?.toUpperCase() + props?.data?.strategy?.slice(1)?.toLowerCase()}`
                : t("explainLink")}
            </Link>
          )
        }
      >
        <Grid
          container
          item
          xs={12}
          gap={"6px"}
          display="flex"
          flexWrap="nowrap"
          flexDirection={"column"}
          justifyContent={"space-between"}
        >
          <Grid container item xs={12} gap={0.5} flexWrap={"nowrap"}>
            <Grid item xs={readMode || !shouldDisplayStrategyOptions ? 12 : 9} marginTop={0.5}>
              {!readMode ? (
                <InputText
                  id="loop_name"
                  placeholder={t("loopNamePlaceholder")}
                  value={props?.data?.loopName}
                  handleChange={(e) => handleLoopNameChange(e)}
                  disabled={Boolean(menuView || readMode)}
                  fullWidth
                  hideDescription
                  max={50}
                />
              ) : (
                <ValueBlock size={12} value={props?.data?.loopName ?? ""} />
              )}
            </Grid>

            {shouldDisplayStrategyOptions && (
              <Grid item xs marginTop={readMode && !menuView ? "-4px" : "1px"}>
                <RadioButton
                  value={`Parallel`}
                  onChange={(e) => updateNodeStrategy(e.target.value)}
                  checked={props?.data?.strategy === "Parallel" || props?.data?.strategy === "PARALLEL"}
                  label={t("labels.parallel")}
                  disabled={Boolean(menuView || readMode)}
                  style={{ marginBottom: "0.2rem" }}
                  name={`Parallel-${uuidv4()}`}
                />

                <RadioButton
                  value={`Combined`}
                  onChange={(e) => updateNodeStrategy(e.target.value)}
                  checked={props?.data?.strategy === "Combined" || props?.data?.strategy === "COMBINED"}
                  disabled={Boolean(menuView || readMode)}
                  label={t("labels.combined")}
                  name={`Combined-${uuidv4()}`}
                />
              </Grid>
            )}
          </Grid>

          {(!readMode || (readMode && props?.data?.input_lists && props?.data?.input_lists?.length > 0)) && (
            <Grid item xs={12}>
              {readMode && props?.data?.input_lists && props?.data?.input_lists?.length > 0 ? (
                <Grid container className={styles.chipsContainer}>
                  {props?.data?.input_lists?.map((list, index) => (
                    <Chip key={index} size="small">
                      {list}
                    </Chip>
                  ))}
                </Grid>
              ) : (
                !readMode && (
                  <InputText
                    startAdornment={
                      <SearchIcon fontSize="small" sx={{ color: "var(--base-text-2)", paddingTop: "5px" }} />
                    }
                    value={value}
                    width={menuView ? 250 : 300}
                    shouldAcceptNewValues
                    disabled={menuView}
                    optionsWithValues={allFeatures
                      ?.filter((feat) => ["LIST", "UNDEFINED"].includes(feat?.type))
                      ?.map((feature) => ({ label: feature?.name, value: feature?.name }))}
                    multipleModeProps={{
                      isActive: true,

                      setSelectedValues: (selectedOptions) => {
                        setNodes(
                          getNodes().map((node) => {
                            const newNode = { ...node }
                            if (node.id === nodeId) {
                              newNode.data = {
                                ...newNode.data,
                                input_lists: [...selectedOptions],
                              }
                            }
                            return newNode
                          }),
                        )
                        setSelectedValues(selectedOptions as string[])
                      },
                      selectedValues: selectedValues,
                    }}
                    handleChange={(e: InputChangeEvent) => setValue(e.target.value)}
                    hideDescription
                    placeholder={t("inputListPlaceholder")}
                    menuProps={{ menuMaxContent: true }}
                    noEntriesProps={{
                      handleAddNewValue: handleAddNewOption,
                      shouldAcceptNewValues: !!value,
                      message: t("inputListNoEntries"),
                    }}
                  />
                )
              )}
            </Grid>
          )}
        </Grid>
      </BaseWorkflowNode>
    </Fragment>
  )
}
